import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import './Activate.module.css'

import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout'
import { StaticPaginatedTable } from '@/components/search/StaticPaginatedTable'
import { SearchInput } from '@/components/search/SearchInput'
import { SearchTable, SearchTableParams, SearchTableRef, PageQuery, PageResponse } from '@/components/search/SearchTable'
import { Spinner } from '@/components/spinner'
import { useAppContext } from '@/context/app'
import { useModal } from '@/components/modal/Modal'
import { CreateMachineWizardPopup, MachineCreateWizard } from '@/popups/machines/wizard/create/Main'
import { InventoryItemEditPopup, InventoryItemRow } from '@/pages/communal/remanent/inventory_items/common'
import { Roles, isAdminRole } from '@/services/repository/users'
import { Machine } from '@/services/model/machine'
import { ActivateMapComponent } from './activate/Map'
import { SimpleMachine } from '../communal/my/MyMachines'

const roles = [Roles.CompanyAdmin, Roles.SuperAdmin, Roles.HyperAdmin, Roles.CompanyManager, Roles.Accountant]

export const QRActivate = () => {
  const { services, session, translates } = useAppContext()
  const navigate = useNavigate()
  const { openModal } = useModal()
  const qrCodeId = useParams().id
  const searchTableRef = useRef<SearchTableRef>(null)
  const [inventoryItems, setInventoryItems] = useState<InventoryItemRow[] | null>(null)
  const [inventorySearch, setInventorySearch] = useState('')
  const [inventoryDebouncedSearch, setInventoryDebouncedSearch] = useState('')
  const allowedUser = useMemo(() => roles.includes(session?.user.role || '' as any), [session?.user.role])

  useEffect(() => {
    if (!session) {
      return
    }
    services.session.fetchWithToken('/api/communal/remanent/inventory-items').then(async (response) => {
      if (response.status !== 200) {
        setInventoryItems([])
        return
      }
      const data = await response.json()
      setInventoryItems(Array.isArray(data) ? data : [])
    })
  }, [services.session, session])

  const filteredInventoryItems = useMemo(() => {
    if (!inventoryItems) {
      return []
    }
    if (!inventoryDebouncedSearch) {
      return inventoryItems
    }
    const phrase = inventoryDebouncedSearch.toLocaleLowerCase()
    return inventoryItems.filter((item) => [
      item.inventoryNumber || '',
      item.serialNumber || '',
      item.status || '',
    ].some((value) => value.toLocaleLowerCase().includes(phrase)))
  }, [inventoryDebouncedSearch, inventoryItems])

  if (!translates || !session) {
    return <Spinner />
  }

  const activateQRCode = async (payload: Record<string, unknown>, redirectTo: string) => {
    const response = await services.session.fetchWithToken('/api/communal/qr/activate', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: qrCodeId,
        ...payload,
      }),
    })
    if (response.ok) {
      navigate(redirectTo)
    }
  }

  const addMachineQRCode = async (machineId: string) => {
    await activateQRCode({
      machineId,
      targetType: 'machine',
      targetId: machineId,
    }, `/qr/tag/${qrCodeId}`)
  }

  const addInventoryItemQRCode = async (inventoryItemId: string) => {
    await activateQRCode({
      targetType: 'inventory_item',
      targetId: inventoryItemId,
    }, '/communal/remanent/inventory-items/manager')
  }

  const reloadInventoryItems = async () => {
    const response = await services.session.fetchWithToken('/api/communal/remanent/inventory-items')
    if (response.status !== 200) {
      setInventoryItems([])
      return
    }
    const data = await response.json()
    setInventoryItems(Array.isArray(data) ? data : [])
  }

  const openCreateInventoryItemPopup = () => {
    openModal((props) => <InventoryItemEditPopup
      {...props}
      onSaved={async (inventoryItemId) => {
        await reloadInventoryItems()
        if (inventoryItemId) {
          await addInventoryItemQRCode(inventoryItemId)
        }
      }}
    />)
  }

  const tableParams: SearchTableParams<SimpleMachine> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleMachine>> => {
      const url = isAdminRole(session.user.role)
        ? `/api/superadmin/machines/search?pageNumber=${q.pageNumber || ''}&pageSize=${q.pageSize || ''}&search=${q.search || ''}`
        : `/api/communal/my/machines/search?pageNumber=${q.pageNumber || ''}&pageSize=${q.pageSize || ''}&search=${q.search || ''}`
      return services.session.fetchWithToken(url).then((r) => {
        if (r.status === 200) {
          return r.json()
        }
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 6,
      search: '',
    },
    toolbox: () => <>
      <div className='tool' onClick={() => openModal((props) => MachineCreateWizard({
        ...props,
        qrCode: qrCodeId,
        onDone: (machineId: string) => {
          navigate(`/communal/machine/${machineId}`)
          props.onClose()
        },
      }))}>
        <i className='svgfont-add' />
      </div>
    </>,
    header: () => <>
      <div className='hcol'></div>
      <div className='hcol'>{session.translates.for('elements.table.machines.manufacturer')}</div>
      <div className='hcol'>{session.translates.for('elements.table.machines.model')}</div>
      <div className='hcol'>{session.translates.for('elements.table.machines.number')}</div>
    </>,
    row: (row) => <>
      <div className='col icocol icocol-active' onClick={() => addMachineQRCode(row.id)}>
        <i className='svgfont-add' />
      </div>
      <div className='col' onClick={() => addMachineQRCode(row.id)}>{row.manufacturer}</div>
      <div className='col' onClick={() => addMachineQRCode(row.id)}>{row.model}</div>
      <div className='col' onClick={() => addMachineQRCode(row.id)}>{row.internalNumber || row.imei || row.id}</div>
    </>,
    translates: session.translates,
  }

  if (!allowedUser) {
    return <LoggedInPageLayout services={services} session={session} pageName='activate-tag'>
      <h3>{translates.for('page.qr_activate.title')}</h3>
      <div className='permission-denide'>{translates.for('errors.no_permissions')}</div>
    </LoggedInPageLayout>
  }

  return <LoggedInPageLayout services={services} session={session} pageName='activate-tag'>
    {[Roles.CompanyAdmin, Roles.SuperAdmin, Roles.HyperAdmin, Roles.CompanyManager].includes(session.user.role as Roles) ? <>
      <h3 className='section'>{translates.for('page.qr_activate.machine_title')}</h3>
      <p className='section'>{translates.for('page.qr_activate.machine_help')}</p>
      <a className='main-btn' onClick={() => openModal((props) => CreateMachineWizardPopup({
        ...props,
        qrCode: qrCodeId,
        onDone: (machineId: string) => {
          navigate(`/communal/machine/${machineId}`)
          props.onClose()
        },
      }))}>
        {translates.for('page.qr_activate.add_btn')}
      </a>

      <h3 className='section'>{translates.for('page.qr_activate.find_section_title')}</h3>
      <div style={{ height: 'min(350px, 80vh)' }}>
        <ActivateMapComponent
          fetchUrl={isAdminRole(session.user.role) ? '/api/communal/map' : '/api/communal/my/map'}
          onSelect={(machine: Machine) => {
            if (machine.id) {
              addMachineQRCode(machine.id)
              return
            }
            openModal((props) => CreateMachineWizardPopup({
              ...props,
              qrCode: qrCodeId,
              machine,
              onDone: (machineId: string) => {
                navigate(`/communal/machine/${machineId}`)
                props.onClose()
              },
            }))
          }}
        />
      </div>
      <SearchTable ref={searchTableRef} {...tableParams} />
    </> : null}

    <h3 className='section'>{translates.for('page.qr_activate.inventory_items_title')}</h3>
    <p className='section'>{translates.for('page.qr_activate.inventory_items_help')}</p>
    <div className='main-btn' onClick={openCreateInventoryItemPopup}>
      {translates.for('elements.table.inventory_items.add')}
    </div>
    <StaticPaginatedTable<InventoryItemRow>
      data={filteredInventoryItems}
      translates={translates}
      toolboxRight={() => <>
        <SearchInput
          translates={translates}
          value={inventorySearch}
          onChange={setInventorySearch}
          onDebouncedChange={setInventoryDebouncedSearch}
        />
      </>}
      header={() => <>
        <div className='hcol'></div>
        <div className='hcol'>{translates.for('elements.table.inventory_items.inventory_number')}</div>
        <div className='hcol'>{translates.for('elements.table.inventory_items.serial_number')}</div>
        <div className='hcol'>{translates.for('elements.table.inventory_items.status')}</div>
      </>}
      row={(row) => <>
        <div className='col icocol icocol-active' onClick={() => addInventoryItemQRCode(row.id)}>
          <i className='svgfont-add' />
        </div>
        <div className='col' onClick={() => addInventoryItemQRCode(row.id)}>{row.inventoryNumber || '-'}</div>
        <div className='col' onClick={() => addInventoryItemQRCode(row.id)}>{row.serialNumber || '-'}</div>
        <div className='col' onClick={() => addInventoryItemQRCode(row.id)}>{translates.for(`elements.table.inventory_items.statuses.${row.status || 'active'}`)}</div>
      </>}
    />
  </LoggedInPageLayout>
}
